import { useEffect, useState } from "react";
import { Pencil, Trash2, RefreshCw, Clock } from "lucide-react";
import { Button } from "./ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { toast } from "sonner";
import { apiListOrders, apiDeleteOrder } from "../lib/api";
import { formatINR } from "../lib/menu";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function PendingOrders({ refreshKey, onEdit }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiListOrders();
      const pending = (res.orders || []).filter(
        (o) => o.payment_method === "Pending Payment"
      );
      setOrders(pending);
    } catch (e) {
      toast.error("Failed to load pending orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [refreshKey]);

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await apiDeleteOrder(toDelete.id);
      toast.success("Order deleted");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-4 items-end justify-between">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight text-[#1A1918] font-heading">
            Pending Orders
          </h2>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5854] mt-1">
            {orders.length} pending {orders.length === 1 ? "order" : "orders"} — not in revenue
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          data-testid="pending-refresh-button"
          className="border-[#E6E2D8] text-[#1A1918] hover:bg-[#F5F2EB] h-10"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </Button>
      </div>

      <div
        data-testid="pending-orders-table"
        className="w-full overflow-x-auto rounded-lg border border-[#F5A623]/40 bg-[#FFF8F0]"
      >
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-[#FFF0D0] border-b border-[#F5A623]/30">
              <th className="text-xs font-bold uppercase tracking-[0.1em] text-[#5C5854] py-4 px-6">When</th>
              <th className="text-xs font-bold uppercase tracking-[0.1em] text-[#5C5854] py-4 px-6">Employee</th>
              <th className="text-xs font-bold uppercase tracking-[0.1em] text-[#5C5854] py-4 px-6">Items</th>
              <th className="text-xs font-bold uppercase tracking-[0.1em] text-[#5C5854] py-4 px-6 text-right">Total</th>
              <th className="text-xs font-bold uppercase tracking-[0.1em] text-[#5C5854] py-4 px-6 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-12 text-center text-[#5C5854] text-sm">
                  {loading ? "Loading..." : "No pending orders."}
                </td>
              </tr>
            ) : (
              orders.map((o) => (
                <tr
                  key={o.id}
                  className="border-b border-[#F5A623]/20 last:border-0 hover:bg-[#FFF0D0]/50"
                  data-testid={`pending-order-row-${o.id}`}
                >
                  <td className="py-4 px-6 text-sm text-[#1A1918] align-top">
                    <div className="flex items-center gap-2">
                      <Clock size={14} className="text-[#F5A623]" />
                      {fmtDate(o.timestamp)}
                    </div>
                  </td>
                  <td className="py-4 px-6 text-sm align-top">
                    <div className="font-semibold text-[#1A1918]">{o.employee}</div>
                    {o.title && <div className="text-xs text-[#5C5854]">{o.title}</div>}
                  </td>
                  <td className="py-4 px-6 text-sm align-top max-w-md">
                    <ul className="space-y-0.5">
                      {(o.items || []).map((it, i) => (
                        <li key={i} className="text-[#1A1918]">
                          <span className="font-semibold">{it.qty}×</span> {it.name}
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="py-4 px-6 text-base text-right font-black tabular-nums text-[#F5A623] align-top font-heading">
                    {formatINR(o.total)}
                  </td>
                  <td className="py-4 px-6 align-top">
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => onEdit?.(o)}
                        data-testid={`edit-pending-${o.id}`}
                        className="text-[#5C5854] hover:text-[#C84C31] transition-colors"
                        aria-label="Edit"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => setToDelete(o)}
                        data-testid={`delete-pending-${o.id}`}
                        className="text-[#5C5854] hover:text-[#F44336] transition-colors"
                        aria-label="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#FFFCF5] border-[#E6E2D8]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this pending order?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the order from the Google Sheet. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="delete-confirm"
              className="bg-[#F44336] hover:bg-[#D32F2F] text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
